import { intervalToDuration, formatDistance } from 'date-fns';
import { enUS } from 'date-fns/locale';

const zeroPad = (num?: number) => String(num).padStart(2, '0');

export const secondsToDuration = (seconds: number) => {
  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
  return `${duration.hours ? zeroPad(duration.hours) + ':' : ''}${zeroPad(
    duration.minutes,
  )}:${zeroPad(duration.seconds)}`;
};

// Short format locale for relative time (used in comments)
const shortFormatLocale = {
  ...enUS,
  formatDistance: (token: string, count: number): string => {
    switch (token) {
      case 'lessThanXSeconds':
        return 'now';
      case 'xSeconds':
        return count < 60 ? 'now' : `${count}s`;
      case 'halfAMinute':
        return 'now';
      case 'lessThanXMinutes':
        return 'now';
      case 'xMinutes':
        return `${count}m`;
      case 'aboutXHours':
      case 'xHours':
        return `${count}h`;
      case 'xDays':
        return `${count}d`;
      case 'aboutXWeeks':
      case 'xWeeks':
        return `${count}w`;
      case 'aboutXMonths':
      case 'xMonths':
        return `${count}mo`;
      case 'aboutXYears':
      case 'xYears':
        return `${count}y`;
      default:
        // Fallback to original for any unhandled tokens
        return enUS.formatDistance?.(token, count, {}) || `${count}`;
    }
  },
};

/**
 * Format a date as a relative time string in short format (e.g., "5m", "2h", "3d")
 * Used for comments and other UI elements where space is limited
 */
export const formatRelativeTimeShort = (date: Date | string): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return 'unknown';
    }

    const now = new Date();
    return formatDistance(dateObj, now, { locale: shortFormatLocale });
  } catch (error) {
    console.error('Error formatting relative time:', error, date);
    return 'unknown';
  }
};
